import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";

import { ENTITY_STATUS, PHONE_TYPE } from "../../constants";
import {
  catalogForEntityType,
  daysUntilExpiry,
  docFiles,
  fileEffectiveStatus,
  formatNit,
  isVerifiableContact,
  isoToday,
  normalizeContactEmails,
  normalizeContactPhones,
} from "../entities/entityLogic";
import {
  DOC_FILE_STATUS_COLOR,
  DOC_FILE_STATUS_LABELS,
  ENTITY_REQUEST_ERROR_LABELS,
  ENTITY_STATUS_COLOR,
  ENTITY_STATUS_LABELS,
  ENTITY_TYPE_LABELS,
  fieldErrorLabel,
  formatDate,
  labelForCode,
  missingFieldsText,
} from "../entities/entityLabels";
import EntityContacts from "../entities/EntityContacts";
import PhoneNumberField from "../generic/PhoneNumberField";
import {
  DEFAULT_PHONE_COUNTRY,
  countryCodeForName,
  splitE164,
} from "../../phoneCountries";
import { getEntityConfig, idTypeOptions } from "../../services/backend/EntityConfigService";
import {
  checkEntityContact,
  entityFilePublicUrl,
  getEntity,
  registerEntity,
  resolveEntityPhone,
  sendEntityOtp,
  verifyEntityOtp,
} from "../../services/backend/EntityService";
import {
  clearRecaptcha,
  confirmPhoneCode,
  isValidPhone,
  phoneAuthErrorMessage,
  sendPhoneCode,
  toE164,
} from "../../services/auth/PhoneVerification";
import { NO_SMS_HINT, phoneSendInfo, useResendCooldown } from "./phoneGate";
import { buildEntityData, entityToForm } from "./entityPayload";
import { useApplicantSession } from "./applicantSession";
import DocumentSlots from "./DocumentSlots";
import EntityLogoUpload from "./EntityLogoUpload";

// Ficha pública de la entidad: por donde entra a mantener sus datos y a renovar
// los documentos que se le vencen. Espejo de ApplicantContinue.
//
// Tres formas de entrar, en este orden:
//   1. el estado del router, justo después de registrarse;
//   2. el enlace mágico del correo (?t=…);
//   3. el login por OTP (correo o celular).
//
// El login resuelve contra los ÍNDICES de contacto, así que sirve CUALQUIER
// correo o celular verificado de la entidad, no sólo el principal.

const RECAPTCHA_ID = "recaptcha-entity-continuar";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const errorText = (code) =>
  code ? labelForCode(ENTITY_REQUEST_ERROR_LABELS, code, "No fue posible completar la operación.") : null;

// El payload del enlace mágico se lee en el cliente SÓLO para saber qué ficha
// pedir. NO es una frontera de seguridad: la firma la valida el backend, y el
// acceso al documento lo gobiernan las reglas de Firestore.
function decodeToken(token) {
  try {
    const [payloadB64] = String(token).split(".");
    const pad = payloadB64.length % 4 === 0 ? "" : "=".repeat(4 - (payloadB64.length % 4));
    const json = atob(payloadB64.replace(/-/g, "+").replace(/_/g, "/") + pad);
    return JSON.parse(json);
  } catch (_) {
    return null;
  }
}

export default function EntityContinue() {
  const navigate = useNavigate();
  const location = useLocation();
  const session = useApplicantSession();

  const [cfg, setCfg] = useState({ entityDocumentCatalog: [], storageBucket: "", smsTestMode: false });
  const [nit, setNit] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);

  // --- Puerta de entrada ---
  const [channel, setChannel] = useState("EMAIL");
  const [loginEmail, setLoginEmail] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpInfo, setOtpInfo] = useState(null);
  const [loginPhone, setLoginPhone] = useState("");
  const [phoneConfirmation, setPhoneConfirmation] = useState(null);
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneInfo, setPhoneInfo] = useState(null);
  const phoneCooldown = useResendCooldown();

  // --- Edición ---
  const [form, setForm] = useState(null);
  // Campo → código de error, para marcar CUÁL quedó mal en vez de decir sólo que
  // algo lo quedó. Los llena el servidor (VALIDATION devuelve errors) y el propio
  // formulario antes de mandar nada.
  const [fieldErrors, setFieldErrors] = useState({});

  // Corregir un campo borra su marca: dejarla mientras se teclea la corrección
  // convierte el aviso en ruido.
  const clearFieldError = (key) =>
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [pending, setPending] = useState({});
  const [expiry, setExpiry] = useState({});
  // Fecha de expedición por documento, para los tipos que la piden
  // (hasIssueDate). Va aparte de `expiry` porque son dos fechas distintas y un
  // tipo puede pedir las dos.
  const [issued, setIssued] = useState({});

  // Verificación de un contacto NUEVO agregado desde la ficha.
  const [verifying, setVerifying] = useState(null); // { channel, contact }
  const [verifyCode, setVerifyCode] = useState("");
  const [verifyConfirmation, setVerifyConfirmation] = useState(null);

  useEffect(() => {
    getEntityConfig().then(setCfg);
    return () => clearRecaptcha();
  }, []);

  const loadEntity = useCallback(async (targetNit) => {
    setLoading(true);
    const data = await getEntity(targetNit);
    setLoading(false);
    if (!data) { setError("No encontramos esa entidad."); return; }
    setEntity(data);
    setForm(entityToForm(data));
    // La vigencia que ya tiene cada documento arranca puesta en su campo. Sin
    // esto el campo salía vacío y reemplazar el archivo sin volver a teclear la
    // fecha se estrellaba contra EXPIRY_DATE_REQUIRED: el intake exige la fecha
    // en todo documento con vigencia. Además el campo pasa a decir la verdad —
    // lo que se va a guardar— en vez de contradecir al pie «Vigencia actual».
    const seeded = {};
    const seededIssued = {};
    for (const [key, entry] of Object.entries(data.documents || {})) {
      const withDate = docFiles(entry).find((f) => f.expiryDate);
      if (withDate) seeded[key] = withDate.expiryDate;
      // Mismo motivo con la expedición: sin sembrarla, reemplazar el archivo
      // sin volver a teclear la fecha se estrella contra ISSUE_DATE_REQUIRED.
      const withIssue = docFiles(entry).find((f) => f.issuedAt);
      if (withIssue) seededIssued[key] = withIssue.issuedAt;
    }
    setExpiry(seeded);
    setIssued(seededIssued);
    setLogoPreview(entityFilePublicUrl(cfg.storageBucket, data.logoPath));
  }, [cfg.storageBucket]);

  // 1. Recién registrada. 2. Enlace mágico.
  useEffect(() => {
    if (nit) return;
    const fromState = location.state?.nit;
    if (fromState) { setNit(fromState); return; }
    const token = new URLSearchParams(location.search).get("t");
    if (token) {
      const payload = decodeToken(token);
      if (payload?.nit) { setNit(payload.nit); setSessionToken(token); }
    }
  }, [location, nit]);

  useEffect(() => { if (nit) loadEntity(nit); }, [nit, loadEntity]);

  // La identidad viaja al Header del layout público.
  useEffect(() => {
    if (!session) return;
    session.setIdentity(
      entity
        ? {
            fullName: entity.legalName,
            photoUrl: entityFilePublicUrl(cfg.storageBucket, entity.logoPath),
            onLogout: handleLogout,
          }
        : null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity, cfg.storageBucket]);

  function handleLogout() {
    setNit(null); setSessionToken(null); setEntity(null); setForm(null);
    setLogo(null); setLogoPreview(null); setPending({}); setExpiry({});
    setOtpSent(false); setOtpCode(""); setOtpInfo(null); setLoginEmail("");
    setPhoneConfirmation(null); setPhoneCode(""); setPhoneInfo(null); setLoginPhone("");
    setError(null); setNotice(null);
    session?.setIdentity(null);
    // Se limpia el estado del router y el ?t= para que un F5 no vuelva a entrar.
    navigate("/submission/entidad/continuar", { replace: true });
  }

  // --- Login -----------------------------------------------------------------

  const handleSendLoginOtp = async () => {
    const dest = loginEmail.trim();
    if (!EMAIL_RE.test(dest)) { setError("Ingresa un correo válido."); return; }
    setBusy(true); setError(null);
    const { ok, json } = await sendEntityOtp({ recipient: dest });
    setBusy(false);
    if (!ok) { setError(errorText(json?.error) || "No fue posible enviar el código."); return; }
    setOtpSent(true);
    setOtpInfo(`Enviamos un código a ${dest}. Vence en ${json?.expiresInMinutes || 10} minutos.`);
  };

  const handleVerifyLoginOtp = async () => {
    setBusy(true); setError(null);
    const { ok, json } = await verifyEntityOtp({ recipient: loginEmail.trim(), code: otpCode.trim() });
    setBusy(false);
    if (!ok) { setError(errorText(json?.error) || "Código incorrecto."); return; }
    if (!json.nit) { setError(errorText("NO_ENTITY")); return; }
    setSessionToken(json.sessionToken);
    setNit(json.nit);
  };

  const handleSendLoginSms = async () => {
    if (!isValidPhone(loginPhone)) { setError("Ingresa un celular válido."); return; }
    setBusy(true); setError(null);
    try {
      const confirmation = await sendPhoneCode(loginPhone, RECAPTCHA_ID, { testMode: !!cfg.smsTestMode });
      setPhoneConfirmation(confirmation);
      setPhoneInfo(phoneSendInfo(toE164(loginPhone), !!cfg.smsTestMode));
      phoneCooldown.start();
    } catch (e) {
      setError(phoneAuthErrorMessage(e));
    } finally { setBusy(false); }
  };

  const handleVerifyLoginSms = async () => {
    setBusy(true); setError(null);
    try {
      const { idToken } = await confirmPhoneCode(phoneConfirmation, phoneCode);
      const { ok, json } = await resolveEntityPhone({ phoneToken: idToken });
      if (!ok) { setError(errorText(json?.error) || "No fue posible entrar."); return; }
      setSessionToken(json.sessionToken);
      setNit(json.nit);
    } catch (e) {
      setError(phoneAuthErrorMessage(e));
    } finally { setBusy(false); }
  };

  // --- Guardado ---------------------------------------------------------------

  const docs = useMemo(
    () => catalogForEntityType(cfg.entityDocumentCatalog, entity?.entityType),
    [cfg.entityDocumentCatalog, entity?.entityType]
  );
  const permanentDocs = docs.filter((d) => !d.hasExpiry);
  const renewableDocs = docs.filter((d) => d.hasExpiry);

  const flattenFiles = () =>
    docs.flatMap((d) =>
      (pending[d.key] || [])
        .filter(Boolean)
        .map(({ file, replaceFileId }) => ({
          type: d.key,
          file,
          ...(d.hasExpiry && expiry[d.key] ? { expiryDate: expiry[d.key] } : {}),
          ...(d.hasIssueDate && issued[d.key] ? { issuedAt: issued[d.key] } : {}),
          ...(replaceFileId ? { replaceFileId } : {}),
        }))
    );

  // Lo que se puede comprobar sin ir al servidor. Mismo formato que sus errores
  // ({ campo: CÓDIGO }) para que los dos se pinten con el mismo código.
  const localErrors = () => {
    const errs = {};
    const f = form || {};
    if (String(f.legalRepEmail || "").trim() && !EMAIL_RE.test(String(f.legalRepEmail).trim())) {
      errs.legalRepEmail = "INVALID_EMAIL";
    }
    const repPhone = String(f.legalRepPhone || "").trim();
    // Un número que empieza por "+" lo compuso el selector: si quedó corto, está
    // a medias. Los de antes del selector se guardaron como texto libre y no se
    // juzgan aquí.
    if (repPhone.startsWith("+") && !/^\+\d{7,15}$/.test(repPhone)) {
      errs.legalRepPhone = "INVALID_PHONE";
    }
    return errs;
  };

  // El teléfono del representante se guarda compuesto (E.164), así que el campo
  // se llena partiéndolo de vuelta; el país arranca en el de la entidad mientras
  // nadie escoja otro. Un número viejo, escrito antes del selector, se devuelve
  // tal cual y sólo se compone si alguien lo edita.
  const legalRepCountry =
    form?.legalRepPhoneCountryCode ||
    countryCodeForName(form?.country || entity?.country) ||
    DEFAULT_PHONE_COUNTRY;
  const legalRepNational = splitE164(form?.legalRepPhone, legalRepCountry).national;

  const save = async ({ removeLogo = false } = {}) => {
    const local = localErrors();
    if (Object.keys(local).length > 0) {
      setFieldErrors(local);
      setError(`Revisa: ${missingFieldsText(local)}.`);
      return;
    }
    setBusy(true); setError(null); setNotice(null); setFieldErrors({});
    const { ok, json } = await registerEntity({
      data: buildEntityData(form),
      files: flattenFiles(),
      logo,
      removeLogo,
      sessionToken,
      idempotencyKey: `entity:${nit}:${Date.now()}`,
    });
    setBusy(false);
    if (!ok) {
      const serverErrors = json?.errors || json?.body?.errors;
      if (serverErrors && typeof serverErrors === "object" && Object.keys(serverErrors).length) {
        setFieldErrors(serverErrors);
        setError(`Revisa: ${missingFieldsText(serverErrors)}.`);
        return;
      }
      setError(errorText(json?.error) || "No fue posible guardar los cambios.");
      return;
    }
    setPending({}); setExpiry({}); setLogo(null);
    setNotice("Cambios guardados.");
    await loadEntity(nit);
  };

  // --- Contactos --------------------------------------------------------------
  //
  // Se editan sobre el formulario y viajan con el siguiente guardado, igual que
  // el resto de la ficha. Un contacto nuevo entra sin verificar; verificarlo es
  // una acción aparte, porque hay que probar que el correo o el celular existe.

  const patchContacts = (channelKey, list) =>
    setForm((f) => ({
      ...f,
      [channelKey === "PHONE" ? "contactPhones" : "contactEmails"]:
        channelKey === "PHONE" ? normalizeContactPhones(list) : normalizeContactEmails(list),
    }));

  const listOf = (channelKey) =>
    channelKey === "PHONE" ? form?.contactPhones || [] : form?.contactEmails || [];

  const handleAddContact = async (channelKey, contact) => {
    // Falla rápido si ya es de otra entidad; el backend lo vuelve a exigir.
    const chk = await checkEntityContact({
      nit,
      email: channelKey === "EMAIL" ? contact.address : "",
      phone: channelKey === "PHONE" && contact.type !== PHONE_TYPE.LANDLINE ? toE164(contact.number) : "",
    });
    if (chk.emailInUse) { setError(errorText("EMAIL_IN_USE")); return; }
    if (chk.phoneInUse) { setError(errorText("PHONE_IN_USE")); return; }
    setError(null);
    patchContacts(channelKey, [...listOf(channelKey), contact]);
    setNotice("Contacto agregado. Verifícalo y guarda los cambios.");
  };

  const handleUpdateContact = (channelKey, id, patch) =>
    patchContacts(channelKey, listOf(channelKey).map((c) => (c.id === id ? { ...c, ...patch } : c)));

  const handleSetPrimary = (channelKey, id) =>
    patchContacts(channelKey, listOf(channelKey).map((c) => ({ ...c, primary: c.id === id })));

  const handleDeleteContact = (channelKey, id) =>
    patchContacts(channelKey, listOf(channelKey).filter((c) => c.id !== id));

  const startVerify = async (channelKey, contact) => {
    setVerifying({ channel: channelKey, contact });
    setVerifyCode(""); setVerifyConfirmation(null); setError(null);
    if (channelKey === "EMAIL") {
      const { ok, json } = await sendEntityOtp({ recipient: contact.address });
      if (!ok) setError(errorText(json?.error) || "No fue posible enviar el código.");
      return;
    }
    if (!isVerifiableContact(contact)) return;
    try {
      const confirmation = await sendPhoneCode(contact.number, RECAPTCHA_ID, { testMode: !!cfg.smsTestMode });
      setVerifyConfirmation(confirmation);
    } catch (e) { setError(phoneAuthErrorMessage(e)); }
  };

  const finishVerify = async () => {
    const { channel: ch, contact } = verifying;
    setBusy(true); setError(null);
    try {
      if (ch === "EMAIL") {
        const { ok, json } = await verifyEntityOtp({ recipient: contact.address, code: verifyCode.trim() });
        if (!ok) { setError(errorText(json?.error) || "Código incorrecto."); return; }
        // El backend confirma la verificación al guardar (mira el doc de OTP
        // reciente); aquí sólo se refleja para que la ficha no mienta.
        handleUpdateContact("EMAIL", contact.id, { verified: true });
        setNotice("Correo verificado. Guarda los cambios para dejarlo registrado.");
      } else {
        const { idToken } = await confirmPhoneCode(verifyConfirmation, verifyCode.trim());
        // El celular se guarda de inmediato: su prueba es un token de un solo
        // uso que no sobrevive a la siguiente pantalla, así que no puede esperar
        // a que la entidad pulse «Guardar».
        const { ok, json } = await registerEntity({
          data: buildEntityData({
            ...form,
            contactPhones: listOf("PHONE").map((c) => (c.id === contact.id ? { ...c, verified: true } : c)),
          }),
          sessionToken,
          phoneToken: idToken,
          idempotencyKey: `entity:${nit}:phone:${contact.id}:${Date.now()}`,
        });
        if (!ok) { setError(errorText(json?.error) || "No fue posible verificar el celular."); return; }
        await loadEntity(nit);
        setNotice("Celular verificado.");
      }
      setVerifying(null);
    } catch (e) {
      setError(phoneAuthErrorMessage(e));
    } finally { setBusy(false); }
  };

  // --- Render -----------------------------------------------------------------

  if (!entity) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 3, md: 6 } }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Continuar mi registro</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Entra con cualquiera de los correos o celulares verificados de tu entidad.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <Paper variant="outlined" sx={{ p: 3 }}>
          <ToggleButtonGroup exclusive fullWidth value={channel} sx={{ mb: 3 }}
            onChange={(_, v) => v && setChannel(v)}>
            <ToggleButton value="EMAIL"><EmailOutlinedIcon sx={{ mr: 1 }} fontSize="small" /> Correo</ToggleButton>
            <ToggleButton value="PHONE"><PhoneAndroidIcon sx={{ mr: 1 }} fontSize="small" /> Celular</ToggleButton>
          </ToggleButtonGroup>

          {channel === "EMAIL" ? (
            <Stack spacing={2}>
              <TextField fullWidth type="email" label="Correo registrado" value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)} disabled={otpSent} />
              {otpInfo && <Alert severity="info">{otpInfo}</Alert>}
              {!otpSent ? (
                <Button variant="contained" disabled={busy || !loginEmail.trim()} onClick={handleSendLoginOtp}>
                  Enviar código
                </Button>
              ) : (
                <Stack spacing={1}>
                  <TextField label="Código" value={otpCode} onChange={(e) => setOtpCode(e.target.value)}
                    inputProps={{ maxLength: 6 }} />
                  <Button variant="contained" disabled={busy || otpCode.trim().length < 6}
                    onClick={handleVerifyLoginOtp}>Entrar</Button>
                  <Button size="small" onClick={() => { setOtpSent(false); setOtpCode(""); setOtpInfo(null); }}>
                    Usar otro correo
                  </Button>
                </Stack>
              )}
            </Stack>
          ) : (
            <Stack spacing={2}>
              <TextField fullWidth label="Celular registrado" value={loginPhone}
                onChange={(e) => setLoginPhone(e.target.value)} disabled={!!phoneConfirmation} />
              {phoneInfo && <Alert severity="info">{phoneInfo}</Alert>}
              {!phoneConfirmation ? (
                <Button variant="contained" disabled={busy || phoneCooldown.seconds > 0 || !loginPhone.trim()}
                  onClick={handleSendLoginSms}>
                  {phoneCooldown.seconds > 0 ? `Reenviar (${phoneCooldown.seconds}s)` : "Enviar código"}
                </Button>
              ) : (
                <Stack spacing={1}>
                  <TextField label="Código" value={phoneCode} onChange={(e) => setPhoneCode(e.target.value)}
                    inputProps={{ maxLength: 6 }} />
                  <Typography variant="caption" color="text.secondary">{NO_SMS_HINT}</Typography>
                  <Button variant="contained" disabled={busy || phoneCode.trim().length < 6}
                    onClick={handleVerifyLoginSms}>Entrar</Button>
                </Stack>
              )}
              <div id={RECAPTCHA_ID} />
            </Stack>
          )}
        </Paper>

        <Button sx={{ mt: 2 }} size="small" href="/submission">Volver al inicio</Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      {/* Cabecera */}
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          {/* Two elements, not one Box with a switching `component`: an <img> may
            not carry a `children` prop AT ALL, and `{!logo && <Icon/>}` still
            passes one — `false` — once the logo exists. React throws in
            assertValidProps and the whole screen dies, so this only ever broke
            for entities that HAVE a logo. */}
          {logoPreview ? (
            <Box
              component="img"
              src={logoPreview}
              alt={entity.legalName}
              sx={{
                width: 96, height: 54, objectFit: "contain", borderRadius: 1, bgcolor: "grey.100",
              }}
            />
          ) : (
            <Box
              sx={{
                width: 96, height: 54, borderRadius: 1, bgcolor: "grey.100",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <BusinessIcon color="disabled" />
            </Box>
          )}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>{entity.legalName}</Typography>
            <Typography variant="body2" color="text.secondary">
              NIT {formatNit(entity.nit)} · {ENTITY_TYPE_LABELS[entity.entityType] || entity.entityType}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
              <Chip size="small" color={ENTITY_STATUS_COLOR[entity.status]}
                label={ENTITY_STATUS_LABELS[entity.status] || entity.status} />
              <Chip size="small" variant="outlined"
                label={`Documentación ${entity.completionPercentage ?? 0}%`} />
            </Stack>
          </Box>
          <Button size="small" color="inherit" startIcon={<LogoutIcon />} onClick={handleLogout}>
            Salir
          </Button>
        </Stack>

        {entity.status === ENTITY_STATUS.PENDING && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Tu registro está en revisión. Puedes seguir completando la documentación mientras tanto.
          </Alert>
        )}
        {entity.status === ENTITY_STATUS.INACTIVE && entity.rejectionReason && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>El registro fue devuelto:</strong> {entity.rejectionReason}
          </Alert>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}
      {busy && <LinearProgress sx={{ mb: 2 }} />}

      {/* Datos */}
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Datos de la entidad</Typography>
        <Grid container spacing={2}>
          {[
            ["tradeName", "Nombre comercial"],
            ["sector", "Sector"],
            ["city", "Ciudad"],
            ["department", "Departamento"],
            ["address", "Dirección"],
            ["website", "Sitio web"],
          ].map(([key, label]) => (
            <Grid item xs={12} sm={6} key={key}>
              <TextField fullWidth label={label} value={form?.[key] || ""}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" gutterBottom>Representante legal</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Nombre completo" value={form?.legalRepName || ""}
              onChange={(e) => setForm((f) => ({ ...f, legalRepName: e.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField select fullWidth label="Tipo de documento" value={form?.legalRepIdType || ""}
              onChange={(e) => setForm((f) => ({ ...f, legalRepIdType: e.target.value }))}>
              {idTypeOptions(cfg).map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth label="Número de documento" value={form?.legalRepIdNumber || ""}
              onChange={(e) => setForm((f) => ({ ...f, legalRepIdNumber: e.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Correo" value={form?.legalRepEmail || ""}
              onChange={(e) => {
                const value = e.target.value;
                setForm((f) => ({ ...f, legalRepEmail: value }));
                clearFieldError("legalRepEmail");
              }}
              error={!!fieldErrors.legalRepEmail}
              helperText={fieldErrorLabel(fieldErrors.legalRepEmail)} />
          </Grid>
          <Grid item xs={12}>
            {/* Una fila entera: son dos controles (país + número) y en media
                columna el número se queda sin espacio en una tablet. */}
            <Box sx={{ maxWidth: 520 }}>
              <PhoneNumberField
                label="Teléfono"
                countryCode={legalRepCountry}
                national={legalRepNational}
                onChange={({ countryCode, e164 }) => {
                  setForm((f) => ({
                    ...f,
                    legalRepPhone: e164,
                    legalRepPhoneCountryCode: countryCode,
                  }));
                  clearFieldError("legalRepPhone");
                }}
                error={!!fieldErrors.legalRepPhone}
                helperText={fieldErrorLabel(fieldErrors.legalRepPhone)}
              />
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" gutterBottom>Logo</Typography>
        <EntityLogoUpload
          currentUrl={logoPreview}
          alt={entity.legalName}
          disabled={busy}
          onChange={(file) => { setLogo(file); setLogoPreview(URL.createObjectURL(file)); }}
          onRemove={() => { setLogo(null); setLogoPreview(null); save({ removeLogo: true }); }}
        />
      </Paper>

      {/* Contactos */}
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Contactos</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Puedes registrar varios correos y teléfonos. Sólo un contacto verificado sirve para
          entrar aquí y para ser el principal.
        </Typography>
        <EntityContacts
          emails={form?.contactEmails || []}
          phones={form?.contactPhones || []}
          country={entity?.country}
          canVerify
          busy={busy}
          onAdd={handleAddContact}
          onUpdate={handleUpdateContact}
          onSetPrimary={handleSetPrimary}
          onDelete={handleDeleteContact}
          onVerify={startVerify}
        />

        {verifying && (
          <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: "grey.50" }}>
            <Typography variant="subtitle2" gutterBottom>
              Verificar {verifying.channel === "EMAIL" ? verifying.contact.address : verifying.contact.number}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField size="small" label="Código" value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value)} inputProps={{ maxLength: 6 }} />
              <Button variant="contained" size="small" disabled={busy || verifyCode.trim().length < 6}
                onClick={finishVerify}>Confirmar</Button>
              <Button size="small" onClick={() => setVerifying(null)}>Cancelar</Button>
            </Stack>
          </Paper>
        )}
        <div id={RECAPTCHA_ID} />
      </Paper>

      {/* Documentos */}
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Documentación</Typography>
        <Stack spacing={3}>
          {permanentDocs.length > 0 && (
            <PortalDocGroup
              title="Documentos permanentes"
              caption="Se cargan una vez y quedan en la ficha."
              docs={permanentDocs} entity={entity} pending={pending} expiry={expiry} issued={issued}
              bucket={cfg.storageBucket} setPending={setPending} setExpiry={setExpiry}
              setIssued={setIssued} busy={busy}
            />
          )}
          {renewableDocs.length > 0 && (
            <PortalDocGroup
              title="Documentos renovables"
              caption="Tienen vigencia. Te avisamos por correo antes de que se venzan."
              docs={renewableDocs} entity={entity} pending={pending} expiry={expiry} issued={issued}
              bucket={cfg.storageBucket} setPending={setPending} setExpiry={setExpiry}
              setIssued={setIssued} busy={busy}
            />
          )}
        </Stack>
      </Paper>

      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 4 }}>
        <Button size="large" variant="contained" disabled={busy} onClick={() => save()}>
          {busy ? "Guardando…" : "Guardar cambios"}
        </Button>
      </Stack>
    </Container>
  );
}

function PortalDocGroup({
  title, caption, docs, entity, pending, expiry, issued, bucket,
  setPending, setExpiry, setIssued, busy,
}) {
  const pick = (docKey) => (slot, file, replaceFileId) =>
    setPending((p) => {
      const list = [...(p[docKey] || [])];
      list[slot] = file ? { file, replaceFileId } : undefined;
      return { ...p, [docKey]: list };
    });

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
      <Typography variant="caption" color="text.secondary">{caption}</Typography>
      <Stack spacing={2} sx={{ mt: 1.5 }}>
        {docs.map((d) => {
          const stored = docFiles(entity.documents?.[d.key]);
          // El estado y los días que faltan se DERIVAN al leer: un documento se
          // vence solo, el día que le toca, sin que nadie escriba nada.
          const soonest = stored
            .map((f) => daysUntilExpiry(f))
            .filter((n) => n !== null)
            .sort((a, b) => a - b)[0];

          return (
            <Paper key={d.key} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{d.label || d.key}</Typography>
                {d.required !== false && <Chip size="small" color="primary" label="Obligatorio" />}
                {stored.map((f) => {
                  const st = fileEffectiveStatus(f);
                  return (
                    <Chip key={f.id} size="small" color={DOC_FILE_STATUS_COLOR[st] || "default"}
                      label={DOC_FILE_STATUS_LABELS[st] || st} />
                  );
                })}
                {soonest !== undefined && soonest >= 0 && (
                  <Chip size="small" color={soonest <= (d.expiryWarningDays || 30) ? "warning" : "default"}
                    variant="outlined" label={`Vence en ${soonest} días`} />
                )}
              </Stack>

              {stored.some((f) => f.rejectionComments) && (
                <Alert severity="error" sx={{ my: 1 }}>
                  {stored.find((f) => f.rejectionComments).rejectionComments}
                </Alert>
              )}

              <DocumentSlots
                docDef={d}
                storedFiles={stored}
                pending={pending[d.key] || []}
                disabled={busy}
                onPick={pick(d.key)}
                onView={(f) => window.open(entityFilePublicUrl(bucket, f.path), "_blank", "noopener")}
              />

              {/* La expedición es obligatoria para el tipo que la pide (el
                  RUT): el intake rechaza la carga sin ella. */}
              {d.hasIssueDate && (
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                  <TextField size="small" type="date" label="Fecha de expedición"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: isoToday() }}
                    value={issued[d.key] || ""}
                    onChange={(e) => setIssued((x) => ({ ...x, [d.key]: e.target.value }))}
                    helperText="Cuándo se expidió el documento. Es obligatoria." />
                  {stored[0]?.issuedAt && (
                    <Typography variant="caption" color="text.secondary">
                      Expedición actual: {formatDate(stored[0].issuedAt)}
                    </Typography>
                  )}
                </Stack>
              )}

              {d.hasExpiry && (
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                  <TextField size="small" type="date" label="Vigente hasta"
                    InputLabelProps={{ shrink: true }}
                    value={expiry[d.key] || ""}
                    onChange={(e) => setExpiry((x) => ({ ...x, [d.key]: e.target.value }))}
                    helperText={`Te avisaremos ${d.expiryWarningDays || 30} días antes.`} />
                  {stored[0]?.expiryDate && (
                    <Typography variant="caption" color="text.secondary">
                      Vigencia actual: {formatDate(stored[0].expiryDate)}
                    </Typography>
                  )}
                </Stack>
              )}
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
}
